import React, { useState } from "react";

import { faXmark } from "@fortawesome/pro-light-svg-icons";

import { useAwsAccountInfo } from "src/adminPanel/queries";
import { useWorkspaces } from "src/api/queries";
import { ExternalLink } from "src/base-components/ExternalLink";
import { LoadingView } from "src/base-components/LoadingView";
import { Tabs } from "src/base-components/Tabs";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { IconButton } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { JobsDebugSection } from "src/powertools/JobsDebugSection";
import { PendingDecisionsDebugSection } from "src/powertools/PendingDecisionsDebugSection";
import { useWsInfo } from "src/powertools/useWsInfo";
import {
  getAgentLogsInsightsLink,
  getCloudFormationStackLink,
  getInfoEndpoint,
  getLambdaLink,
  getS3BucketLink,
} from "src/powertools/utils";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type Props = {
  onRequestClose?: () => void;
};

type DebugToolsProps = {
  workspace: WorkspaceDataplane;
};

export const DebugTools: React.FC<DebugToolsProps> = ({ workspace }) => {
  const wsInfo = useWsInfo(workspace);
  const awsAccountInfo = useAwsAccountInfo(workspace.aws_account_id || "");
  const [accessLevel, setAccessLevel] = useState<string>("core-engineer");
  const cfnStackName = wsInfo.data?.stack_name || "";
  const region = wsInfo.data?.region || "";
  const lambdas = wsInfo.data?.function_name
    ? {
        router: wsInfo.data.function_name,
        runner: wsInfo.data.function_name.replace("router", "runner"),
        copilot: wsInfo.data.function_name.replace("router", "copilot"),
        connect: wsInfo.data.function_name.replace("flow-router", "connect"),
        exporter: wsInfo.data.function_name.replace("flow-router", "exporter"),
        archiver: wsInfo.data.function_name.replace("flow-router", "archiver"),
        stitcher: wsInfo.data.function_name.replace("flow-router", "stitcher"),
        llm_inference: `llm-inference-${cfnStackName}-lambda`,
        files: `files-${cfnStackName}-lambda`,
        batch_archiver: `history-${cfnStackName}v2-batch-archiver`,
        history_exporter: `history-exporter-${cfnStackName}v2`,
      }
    : {
        router: "",
        runner: "",
        copilot: "",
        connect: "",
        exporter: "",
        archiver: "",
        batch_archiver: "",
        migrator: "",
        llm_inference: "",
        files: "",
        history_exporter: "",
      };

  const accountName = awsAccountInfo.data?.name || "";
  const accountNameWithSuffix =
    accountName && accessLevel ? `${accountName}.${accessLevel}` : "";

  const handleCopy = (value: string) => {
    if (value) {
      navigator.clipboard.writeText(value);
      toastActions.success({
        title: "Copied to clipboard",
        description: value,
      });
    }
  };

  return (
    <div className="justify-around px-10">
      <h1>Summary</h1>
      <div className="mb-5 font-inter-normal-12px text-gray-500">
        <span className="font-bold">AWS Region: </span> {workspace.aws_region}
        <br />
        <span className="font-bold">AWS Account: </span>
        {awsAccountInfo.isLoading && <span>Loading...</span>}
        {awsAccountInfo.isError && <span>Error loading account info</span>}
        {awsAccountInfo.data && (
          <>
            {awsAccountInfo.data.account_id}
            {awsAccountInfo.data.name ? ` (${awsAccountInfo.data.name})` : null}
          </>
        )}
        <br />
        <span className="font-bold">Lambda version: </span>{" "}
        {workspace.flow_services_version}
        <br />
        <span className="font-bold">STATUS: </span> {workspace.status}
        <br />
        <span className="font-bold">Stack last event at: </span>{" "}
        {wsInfo.data?.last_updated_at || "N/A"}
        <br />
        <span className="font-bold">Info endpoint: </span>{" "}
        <ExternalLink href={getInfoEndpoint(workspace)}>here</ExternalLink>
      </div>
      <h1>On call tools</h1>
      <div className="font-inter-normal-12px text-gray-500">
        {awsAccountInfo.data && (
          <>
            <div className="my-2 flex flex-row items-center gap-2">
              <label className="mr-2 font-bold" htmlFor="access-level-select">
                Access:
              </label>
              <select
                className="rounded border px-2 py-1 text-black"
                id="access-level-select"
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value)}
              >
                <option value="core-engineer">core-engineer</option>
                <option value="on-call-engineer-read">
                  on-call-engineer-read
                </option>
                <option value="on-call-engineer-write">
                  on-call-engineer-write
                </option>
                <option value="admin">admin</option>
              </select>
              <button
                aria-label="Copy login account name"
                className="rounded bg-indigo-500 px-3 py-1 text-white hover:bg-indigo-600"
                disabled={!accountNameWithSuffix}
                type="button"
                onClick={() => handleCopy("login " + accountNameWithSuffix)}
              >
                Copy login cmd
              </button>
              <button
                aria-label="Copy assume account name"
                className="rounded bg-green-500 px-3 py-1 text-white hover:bg-green-600"
                disabled={!accountNameWithSuffix}
                type="button"
                onClick={() => handleCopy("assume " + accountNameWithSuffix)}
              >
                Copy assume cmd
              </button>
            </div>
            <span className="font-inter-normal-12px text-gray-400">
              Format: <code>{accountNameWithSuffix}</code>
            </span>
            <br />
            <br />
          </>
        )}
        <p>
          Click{" "}
          <ExternalLink href={getCloudFormationStackLink(cfnStackName, region)}>
            here
          </ExternalLink>{" "}
          to access the CloudFormation stack.
        </p>
        {wsInfo.data?.error_log_insights_query_link && (
          <p>
            Click{" "}
            <ExternalLink href={wsInfo.data.error_log_insights_query_link}>
              here
            </ExternalLink>{" "}
            to access Error Log Insights.
          </p>
        )}
        <p>
          Click{" "}
          <ExternalLink href={getAgentLogsInsightsLink(cfnStackName, region)}>
            here
          </ExternalLink>{" "}
          to access Agent Logs.
        </p>
        {Object.entries(lambdas).map(([key, lambda]) => (
          <p key={key}>
            Click{" "}
            <ExternalLink href={getLambdaLink(region, lambda)}>
              here
            </ExternalLink>{" "}
            to access the {key} Lambda function on the AWS console
          </p>
        ))}
        <p>
          Click{" "}
          <ExternalLink
            href={getS3BucketLink(`files-${cfnStackName}-bucket`, region)}
          >
            here
          </ExternalLink>{" "}
          to access the files S3 bucket on the AWS console
        </p>
      </div>
      {workspace.base_url && (
        <div className="mt-8 border-gray-200 border-t pt-6">
          <Tabs
            defaultActiveKey="jobs"
            tabListClassName="flex gap-x-4 border-gray-200 border-b mb-4"
            tabs={[
              {
                key: "jobs",
                label: "Jobs",
                content: <JobsDebugSection workspace={workspace} />,
              },
              {
                key: "pending-decisions",
                label: "Pending Decisions",
                content: <PendingDecisionsDebugSection workspace={workspace} />,
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

export const DebugSidePane: React.FC<Props> = ({ onRequestClose }) => {
  const workspacesResult = useWorkspaces();
  const { wsId } = useParamsDecode<DashboardPageParams>();

  const currentWs = workspacesResult.data?.find((ws) => ws.id === wsId);

  const renderHeader = () => (
    <div className="mb-5 flex items-start justify-around px-10">
      <div className="grow">
        <div className="mb-3 font-inter-semibold-16px text-gray-800">
          @taktile Debug Pane
        </div>
      </div>
      {onRequestClose && (
        <IconButton icon={faXmark} size="md" onClick={onRequestClose} />
      )}
    </div>
  );

  return (
    <div className="flex h-full w-full max-w-7xl flex-col border-gray-200 border-l bg-white py-8">
      {renderHeader()}
      <div className="mt-4 min-h-0 flex-1 overflow-auto">
        <LoadingView
          queryResult={workspacesResult}
          renderUpdated={() =>
            currentWs ? <DebugTools workspace={currentWs} /> : <div />
          }
        />
      </div>
    </div>
  );
};
